import { Box, Step, StepLabel, Stepper } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { StepperBox, StepperSx, StepSx } from '../../../components/Form/FormComponents';
import { IField } from '../../../data-models/field2.data-model';
import { field3ToFormField } from '../../../data-models/field3.data-model';
import { createFormFromFieldsArray } from '../../../view-models/form.view-model';
import { getInitialValues } from '../../Config/Common/ConfigModal';
import { getDefaultValues } from '../../Config/KPI/ConfigKPI';
import { useGetKpiFields } from '../../Config/KPI/KPIFields';
import { KpiFieldDisplayStep } from './KpiFieldDisplayStep';
import { KpiFieldFormStep1 } from './KpiFieldFormStep1';
import { kpiFieldFormStepState, useResetKpiFieldFormState } from './KpiFieldFormStateJ';

const STEP_LABELS = ['Field Settings', 'Set Field Display'];

interface KpiFieldFormProps {
  /** Existing KPI to edit; omit for create. */
  item?: IField<unknown>;
  onClose: () => void;
}

/**
 * Two-step KPI create/edit flow for the Field Manager: step 1 is the standard
 * schema-driven KPI form, step 2 registers the KPI's Company Views display
 * (fieldDisplayConfig). The KPI itself is only saved on the final step.
 */
export function KpiFieldForm({ item, onClose }: KpiFieldFormProps) {
  const kpiFields = useGetKpiFields();
  const formFields = useMemo(() => kpiFields.map((f) => field3ToFormField(f)), [kpiFields]);
  const staticForm = useMemo(
    () =>
      createFormFromFieldsArray<IField<unknown>>(formFields, {
        data: (item ?? getDefaultValues()) as IField<unknown>,
      }),
    [formFields, item]
  );
  // Stable identity: a fresh `values` object every render would make RHF reset
  // (and re-render) endlessly.
  const initialValues = useMemo(
    () => getInitialValues(staticForm) as unknown as IField<unknown>,
    [staticForm]
  );
  const methods = useForm<IField<unknown>>({ values: initialValues });
  const step = useAtomValue(kpiFieldFormStepState);
  const resetFormState = useResetKpiFieldFormState();

  useEffect(() => {
    return resetFormState;
  }, [resetFormState]);

  return (
    <BasicDialog open title={item ? 'Edit KPI' : 'Create KPI'} onClose={onClose}>
      <FormProvider {...methods}>
        {/* Same width as ConfigModal so KPI forms look identical everywhere. */}
        <Box sx={{ width: '50vw' }}>
          <StepperBox>
            <Stepper activeStep={step} sx={StepperSx}>
              {STEP_LABELS.map((label) => (
                <Step key={label} sx={StepSx}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {step === 0 ? (
              <KpiFieldFormStep1 form={staticForm} onCancel={onClose} />
            ) : (
              <KpiFieldDisplayStep item={item} formData={staticForm.data} />
            )}
          </StepperBox>
        </Box>
      </FormProvider>
    </BasicDialog>
  );
}
